import { fetchGradiumToken } from "../gradiumTokenClient";
import type { SttBackend, SttRecognitionOptions } from "./types";

const SUPPORTED_PCM_RATES = [8000, 16000, 22050, 24000, 44100, 48000];
const PREFERRED_SAMPLE_RATE = 24000;
const CHUNK_SECONDS = 0.08;
const PROCESSOR_BUFFER_SIZE = 4096;
const END_OF_STREAM_GRACE_MS = 3000;

type GradiumServerMessage =
  | { type: "ready" }
  | { type: "text"; text: string; start_s?: number }
  | { type: "end_text"; stop_s?: number }
  | { type: "step" }
  | { type: "flushed" }
  | { type: "end_of_stream" }
  | { type: "error"; message?: string };

interface Session {
  socket: WebSocket;
  audioContext: AudioContext;
  stream: MediaStream;
  source: MediaStreamAudioSourceNode;
  processor: ScriptProcessorNode;
  ready: boolean;
  pending: Int16Array[];
  pendingSamples: number;
  chunkSamples: number;
  buffer: string;
  timedOut: boolean;
  ended: boolean;
  silenceTimer: ReturnType<typeof setTimeout> | null;
  noSpeechTimer: ReturnType<typeof setTimeout> | null;
  endTimer: ReturnType<typeof setTimeout> | null;
  opts: SttRecognitionOptions;
}

let active: Session | null = null;
let supportProbe: Promise<boolean> | null = null;

const hasBrowserPrerequisites = (): boolean =>
  typeof WebSocket !== "undefined" &&
  typeof navigator !== "undefined" &&
  typeof navigator.mediaDevices?.getUserMedia === "function" &&
  typeof AudioContext !== "undefined";

async function probeSupport(): Promise<boolean> {
  if (!hasBrowserPrerequisites()) return false;
  try {
    await fetchGradiumToken();
    return true;
  } catch {
    return false;
  }
}

function floatToInt16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const clamped = Math.max(-1, Math.min(1, input[i]));
    out[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
  }
  return out;
}

function int16ToBase64(samples: Int16Array): string {
  const bytes = new Uint8Array(samples.buffer, samples.byteOffset, samples.byteLength);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

function createAudioContext(): AudioContext {
  try {
    const ctx = new AudioContext({ sampleRate: PREFERRED_SAMPLE_RATE });
    if (SUPPORTED_PCM_RATES.includes(ctx.sampleRate)) return ctx;
    void ctx.close();
  } catch {
    return new AudioContext();
  }
  return new AudioContext();
}

function disconnectQuietly(node: AudioNode): void {
  try {
    node.disconnect();
  } catch {
    return;
  }
}

function clearTimers(session: Session): void {
  if (session.silenceTimer) clearTimeout(session.silenceTimer);
  if (session.noSpeechTimer) clearTimeout(session.noSpeechTimer);
  if (session.endTimer) clearTimeout(session.endTimer);
  session.silenceTimer = null;
  session.noSpeechTimer = null;
  session.endTimer = null;
}

function stopCapture(session: Session): void {
  session.processor.onaudioprocess = null;
  disconnectQuietly(session.source);
  disconnectQuietly(session.processor);
  session.stream.getTracks().forEach((track) => track.stop());
  void session.audioContext.close().catch(() => undefined);
}

function finishSession(session: Session): void {
  if (session.ended) return;
  session.ended = true;
  clearTimers(session);
  stopCapture(session);
  if (
    session.socket.readyState === WebSocket.OPEN ||
    session.socket.readyState === WebSocket.CONNECTING
  ) {
    session.socket.close();
  }
  if (active === session) active = null;
  const final = session.buffer.trim();
  if (final) session.opts.onFinalResult?.(final);
  session.opts.onEnd?.({ viaTimeout: session.timedOut });
}

function requestEnd(session: Session, viaTimeout: boolean): void {
  if (session.ended || session.endTimer) return;
  session.timedOut = viaTimeout;
  if (session.silenceTimer) clearTimeout(session.silenceTimer);
  if (session.noSpeechTimer) clearTimeout(session.noSpeechTimer);
  session.silenceTimer = null;
  session.noSpeechTimer = null;
  stopCapture(session);
  if (session.socket.readyState === WebSocket.OPEN) {
    flushPending(session, true);
    session.socket.send(JSON.stringify({ type: "end_of_stream" }));
    session.endTimer = setTimeout(() => finishSession(session), END_OF_STREAM_GRACE_MS);
  } else {
    finishSession(session);
  }
}

function armSilenceTimer(session: Session): void {
  if (session.silenceTimer) clearTimeout(session.silenceTimer);
  session.silenceTimer = setTimeout(() => requestEnd(session, true), session.opts.silenceTimeout);
}

function armNoSpeechTimer(session: Session): void {
  const timeout = session.opts.noSpeechTimeout ?? Math.max(session.opts.silenceTimeout, 10000);
  if (session.noSpeechTimer) clearTimeout(session.noSpeechTimer);
  session.noSpeechTimer = setTimeout(() => requestEnd(session, true), timeout);
}

function flushPending(session: Session, force = false): void {
  if (!session.ready || session.socket.readyState !== WebSocket.OPEN) return;
  while (session.pendingSamples >= session.chunkSamples || (force && session.pendingSamples > 0)) {
    const size = Math.min(session.chunkSamples, session.pendingSamples);
    const chunk = new Int16Array(size);
    let filled = 0;
    while (filled < size && session.pending.length > 0) {
      const head = session.pending[0];
      const take = Math.min(head.length, size - filled);
      chunk.set(head.subarray(0, take), filled);
      filled += take;
      if (take === head.length) {
        session.pending.shift();
      } else {
        session.pending[0] = head.subarray(take);
      }
    }
    session.pendingSamples -= size;
    session.socket.send(JSON.stringify({ type: "audio", audio: int16ToBase64(chunk) }));
  }
}

function handleMessage(session: Session, raw: string): void {
  let message: GradiumServerMessage;
  try {
    message = JSON.parse(raw) as GradiumServerMessage;
  } catch {
    return;
  }
  switch (message.type) {
    case "ready":
      session.ready = true;
      flushPending(session);
      session.opts.onStart?.();
      armNoSpeechTimer(session);
      break;
    case "text":
      if (session.noSpeechTimer) {
        clearTimeout(session.noSpeechTimer);
        session.noSpeechTimer = null;
      }
      session.buffer = [session.buffer, message.text.trim()].filter(Boolean).join(" ");
      session.opts.onInterimResult?.(session.buffer);
      if (!session.endTimer) armSilenceTimer(session);
      break;
    case "end_of_stream":
      finishSession(session);
      break;
    case "error":
      session.opts.onError?.(new Error(`Gradium STT error: ${message.message ?? "unknown"}`));
      finishSession(session);
      break;
    default:
      break;
  }
}

export const gradiumSttBackend: SttBackend = {
  name: "gradium",

  isSupported(): Promise<boolean> {
    if (!supportProbe) supportProbe = probeSupport();
    return supportProbe;
  },

  async startRecognition(opts: SttRecognitionOptions): Promise<void> {
    if (active) {
      await gradiumSttBackend.stopRecognition();
    }
    if (!hasBrowserPrerequisites()) {
      const err = new Error("Gradium STT needs WebSocket, AudioContext and getUserMedia");
      opts.onError?.(err);
      throw err;
    }

    let session: Session | null = null;
    try {
      const { token, sttWsUrl, language } = await fetchGradiumToken(opts.language);
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
      });
      const audioContext = createAudioContext();
      const sampleRate = audioContext.sampleRate;
      if (!SUPPORTED_PCM_RATES.includes(sampleRate)) {
        stream.getTracks().forEach((track) => track.stop());
        void audioContext.close();
        throw new Error(`Gradium STT: unsupported capture sample rate ${sampleRate}`);
      }
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(PROCESSOR_BUFFER_SIZE, 1, 1);
      const url = new URL(sttWsUrl);
      url.searchParams.set("token", token);
      const socket = new WebSocket(url.toString());

      const created: Session = {
        socket,
        audioContext,
        stream,
        source,
        processor,
        ready: false,
        pending: [],
        pendingSamples: 0,
        chunkSamples: Math.round(sampleRate * CHUNK_SECONDS),
        buffer: "",
        timedOut: false,
        ended: false,
        silenceTimer: null,
        noSpeechTimer: null,
        endTimer: null,
        opts,
      };
      session = created;
      active = created;

      processor.onaudioprocess = (event) => {
        const samples = floatToInt16(event.inputBuffer.getChannelData(0));
        created.pending.push(samples);
        created.pendingSamples += samples.length;
        flushPending(created);
      };
      source.connect(processor);
      processor.connect(audioContext.destination);

      socket.onopen = () => {
        socket.send(
          JSON.stringify({
            type: "setup",
            model_name: "default",
            input_format: `pcm_${sampleRate}`,
            json_config: { language },
          }),
        );
      };
      socket.onmessage = (event) => {
        if (typeof event.data === "string") handleMessage(created, event.data);
      };
      socket.onerror = () => {
        opts.onError?.(new Error("Gradium STT websocket error"));
        finishSession(created);
      };
      socket.onclose = () => finishSession(created);
    } catch (err) {
      if (session) finishSession(session);
      const error = err instanceof Error ? err : new Error(String(err));
      opts.onError?.(error);
      throw error;
    }
  },

  stopRecognition(): Promise<void> {
    if (active) requestEnd(active, false);
    return Promise.resolve();
  },
};
